// src/components/Peek.js — A floating window for looking at a contact, firm
// or event without leaving the current page.
//
// Any component inside <PeekProvider> can open one with usePeek().open(type,
// id), or render <PeekLink type id>name</PeekLink>. Links inside a peek open
// the next record in the same window, stacked: Back returns to the previous
// one. Esc or a click outside closes the whole stack. Each record has an
// "Open full page" button for when you do want to leave.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import { dateRange, formatAum, formatDate, oneLine } from '../lib/searchTypes'
import { firmsOf } from '../lib/contactFirms'

const PeekContext = createContext(null)

// Where "Open full page" goes for each type.
const FULL_PAGE = {
  contact: id => `/individuals?open=${id}`,
  firm:    id => `/firms?open=${id}`,
  event:   id => `/events/${id}`,
}

export function usePeek() {
  return useContext(PeekContext)
}

// A name that opens a peek. Outside a PeekProvider it renders plain text.
export function PeekLink({ type, id, children, style }) {
  const peek = usePeek()
  if (!peek || !id) return children
  return (
    <button type="button" onClick={e => { e.stopPropagation(); peek.open(type, id) }}
      style={{
        background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left',
        color: 'var(--blue-text)', cursor: 'pointer', ...style,
      }}>
      {children}
    </button>
  )
}

export function PeekProvider({ children }) {
  const [stack, setStack] = useState([])
  const location = useLocation()

  // From the page this starts the stack (the page is covered while a peek is
  // open); from inside a peek it stacks the next record.
  const open = useCallback((type, id) => setStack(s => {
    const top = s[s.length - 1]
    if (top && top.type === type && top.id === id) return s
    return [...s, { type, id }]
  }), [])
  const back = useCallback(() => setStack(s => s.slice(0, -1)), [])
  const close = useCallback(() => setStack([]), [])

  // Leaving the page (including "Open full page") closes the peek.
  useEffect(() => { setStack([]) }, [location.pathname, location.search])

  const value = useMemo(() => ({ open, back, close }), [open, back, close])
  return (
    <PeekContext.Provider value={value}>
      {children}
      {stack.length > 0 && <PeekWindow stack={stack} onBack={back} onClose={close} />}
    </PeekContext.Provider>
  )
}

function PeekWindow({ stack, onBack, onClose }) {
  const navigate = useNavigate()
  const top = stack[stack.length - 1]

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const Body = { contact: ContactPeek, firm: FirmPeek, event: EventPeek }[top.type]

  return (
    <div onMouseDown={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div role="dialog" aria-modal="true" style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
        width: '100%', maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {stack.length > 1 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onBack} title="Back to the previous record">← Back</button>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            {top.type}
          </span>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(FULL_PAGE[top.type](top.id))}>
            Open full page ↗
          </button>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close" style={{ fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>
          <Body key={`${top.type}-${top.id}`} id={top.id} />
        </div>
      </div>
    </div>
  )
}

// ── Shared pieces ─────────────────────────────────────────────────────────

const list = v => [].concat(v ?? []).filter(x => x != null && x !== '')

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><div className="spinner" /></div>
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--text-secondary)', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  )
}

function Fields({ rows }) {
  const shown = rows.filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
  if (!shown.length) return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', fontSize: 13, marginTop: 14 }}>
      {shown.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ color: 'var(--text-muted)' }}>{k}</div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{v}</div>
        </div>
      ))}
    </div>
  )
}

const Badges = ({ values, cls = 'badge-gray' }) =>
  list(values).map(v => <span key={v} className={`badge ${cls}`} style={{ marginRight: 4, marginBottom: 3 }}>{v}</span>)

function useOwners() {
  const { data } = useQuery({
    queryKey: ['relationship-owners'],
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  return ids => list(ids).map(id => (data ?? []).find(u => u.id === id)?.name).filter(Boolean).join(', ')
}

function EventList({ params }) {
  const { data, isLoading } = useQuery({
    queryKey: ['events-attended', params],
    queryFn: () => api.get('/api/events', { params }).then(r => r.data.data ?? []),
  })
  if (isLoading) return <div className="spinner" />
  if (!data?.length) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>None recorded.</div>
  return data.map(ev => (
    <div key={ev.event_id} style={{ fontSize: 13, marginBottom: 4 }}>
      <PeekLink type="event" id={ev.event_id}>{ev.name}</PeekLink>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
        {' '}· {ev.event_type}{ev.start_date ? ` · ${dateRange(ev.start_date, ev.end_date)}` : ''}
        {ev.via?.length ? ` · via ${ev.via.join(', ')}` : ''}
      </span>
    </div>
  ))
}

function Activity({ rows }) {
  if (!rows?.length) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No interactions recorded.</div>
  return rows.slice(0, 8).map(i => (
    <div key={i.interaction_id} style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 4, alignItems: 'baseline' }}>
      <span className="badge badge-gray" style={{ flexShrink: 0 }}>{i.type}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {i.subject_title || '(no subject)'}
      </span>
      <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>{formatDate(i.date)}</span>
    </div>
  ))
}

// ── Contact ───────────────────────────────────────────────────────────────

function ContactPeek({ id }) {
  const owners = useOwners()
  const { data: c, isLoading, error } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => api.get(`/api/contacts/${id}`).then(r => r.data.data),
  })
  const { data: activity } = useQuery({
    queryKey: ['interactions', { contact_id: id }],
    queryFn: () => api.get('/api/interactions', { params: { contact_id: id } }).then(r => r.data.data ?? []),
  })
  if (isLoading) return <Loading />
  if (error || !c) return <div style={{ fontSize: 13 }}>Could not load this contact.</div>

  return (
    <>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{c.name}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
        {/* Each firm with the title there (migrations/076); a title with no firm shows alone */}
              {!firmsOf(c).length && c.title_role && <div>{c.title_role}</div>}
              {firmsOf(c).map(f => (
                <div key={f.firm_id}>
                  {f.title_role && <span>{f.title_role}, </span>}
                  <PeekLink type="firm" id={f.firm_id}>{f.name}</PeekLink>
                </div>
              ))}
      </div>
      <div style={{ marginTop: 8 }}><Badges values={c.contact_type} cls="badge-blue" /></div>
      <Fields rows={[
        ['Email', list(c.email).map(e => <div key={e}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>)],
        ['Phone', list(c.phone).join(', ')],
        ['Owner', owners(c.relationship_owner)],
        ['Introduced by', c.introducer && <PeekLink type="contact" id={c.introducer.contact_id}>{c.introducer.name}</PeekLink>],
        ['Last interaction', c.last_interaction && formatDate(c.last_interaction)],
        ['Tags', list(c.tags).length > 0 && <Badges values={c.tags} />],
      ]} />
      {c.notes && <Section title="Notes"><div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.notes}</div></Section>}
      <Section title="Events"><EventList params={{ contact_id: id }} /></Section>
      <Section title="Recent activity"><Activity rows={activity} /></Section>
    </>
  )
}

// ── Firm ──────────────────────────────────────────────────────────────────

function FirmPeek({ id }) {
  const owners = useOwners()
  const { data: f, isLoading, error } = useQuery({
    queryKey: ['firm', id],
    queryFn: () => api.get(`/api/firms/${id}`).then(r => r.data.data),
  })
  const { data: people } = useQuery({
    queryKey: ['contacts', { firm_id: id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: id } }).then(r => r.data.data ?? []),
  })
  const { data: interest } = useQuery({
    queryKey: ['firm-interest', id],
    queryFn: () => api.get(`/api/firms/${id}/interest`).then(r => r.data.data ?? []),
  })
  const { data: activity } = useQuery({
    queryKey: ['firm-interactions', id],
    queryFn: () => api.get(`/api/firms/${id}/interactions`).then(r => r.data.data ?? []),
  })
  if (isLoading) return <Loading />
  if (error || !f) return <div style={{ fontSize: 13 }}>Could not load this firm.</div>

  // Taxonomy interests across the firm's records, by dimension.
  const byDim = {}
  for (const rec of interest ?? []) {
    for (const [dim, tags] of Object.entries(rec.by_dimension ?? {})) {
      byDim[dim] = [...new Set([...(byDim[dim] ?? []), ...tags.map(t => t.label)])]
    }
  }

  return (
    <>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{f.name}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
        {[list(f.firm_type).join(', '), f.investor_subtype, f.hq_location].filter(Boolean).join(' · ')}
      </div>
      <Fields rows={[
        ['AUM / fund size', f.aum_fund_size != null && formatAum(f.aum_fund_size)],
        ['Website', f.website && <a href={/^https?:\/\//i.test(f.website) ? f.website : `https://${f.website}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{f.website}</a>],
        ['Owner', owners(f.relationship_owner)],
        ['Family', list(f.principal_families).join(', ')],
        ['Invested in', list(f.known_investments).map(i => i?.name).filter(Boolean).join(', ')],
        ['Tags', list(f.tags).length > 0 && <Badges values={f.tags} />],
      ]} />
      {Object.keys(byDim).length > 0 && (
        <Section title="Investment interest">
          {Object.entries(byDim).map(([dim, labels]) => (
            <div key={dim} style={{ fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: 'var(--text-muted)' }}>{dim}: </span>{labels.join(', ')}
            </div>
          ))}
        </Section>
      )}
      {f.enrichment_summary && (
        <Section title="Summary">
          <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.enrichment_summary}</div>
        </Section>
      )}
      <Section title={`People (${people?.length ?? 0})`}>
        {!people?.length ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts.</div>
          : people.map(p => (
            <div key={p.contact_id} style={{ fontSize: 13, marginBottom: 4 }}>
              <PeekLink type="contact" id={p.contact_id}>{p.name}</PeekLink>
              {p.title_role && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {p.title_role}</span>}
            </div>
          ))}
      </Section>
      <Section title="Events"><EventList params={{ firm_id: id }} /></Section>
      <Section title="Recent activity"><Activity rows={activity} /></Section>
    </>
  )
}

// ── Event ─────────────────────────────────────────────────────────────────

function EventPeek({ id }) {
  const { data: ev, isLoading, error } = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get(`/api/events/${id}`).then(r => r.data.data),
  })
  if (isLoading) return <Loading />
  if (error || !ev) return <div style={{ fontSize: 13 }}>Could not load this event.</div>
  const inCrm = (ev.attendees ?? []).filter(a => a.kind !== 'person').length
  return (
    <>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{ev.name}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
        {[ev.event_type, ev.organizer, ev.start_date && dateRange(ev.start_date, ev.end_date)].filter(Boolean).join(' · ')}
      </div>
      <Fields rows={[
        ['Location', oneLine(ev.location)],
        ['Attendees', `${ev.attendees?.length ?? 0} (${inCrm} in the CRM)`],
        ['Files', (ev.documents ?? []).map(d => (
          <div key={d.document_id}>
            {d.signed_url ? <a href={d.signed_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{d.file_name}</a> : d.file_name}
          </div>
        ))],
        ['Tags', list(ev.topics).length > 0 && <Badges values={ev.topics} cls="badge-blue" />],
      ]} />
    </>
  )
}
